<template>
  <div
    v-if="!!top"
    ref="el"
    :class="[$style.base, { [$style.moved]: isMoved }]"
    :style="{ top: `${top}px`, left }"
  >
    <transition
      name="pop"
      appear
      :duration="9999"
    >
      <div :class="$style.inner">
        <div
          :class="[$style.controls, $style.left]"
          @click="isMoved = !isMoved"
        >
          <BaseIcon
            size="s"
            :color="color"
            :name="isMoved ? 'arrow-right' : 'arrow-left'"
          />
        </div>
        <BaseText :color="color">
          {{ showCopied ? 'Copied!' : company }}
        </BaseText>
        <div :class="[$style.controls, $style.right]">
          <BaseIcon
            size="s"
            :color="color"
            name="copy"
            background="transparent"
            @click="copyToClipboard"
          />
          <BaseIcon
            size="s"
            :color="color"
            name="eye-off"
            background="transparent"
            @click="stopImpersonating"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useElementBounding } from '@vueuse/core';
import config from '@/config';
import { useCompanyStore } from '@/stores/company';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useThemeStore } from '@/stores/theme';

const backendUrl = config.backendUrl;
const companyStore = useCompanyStore();

const company = computed(
  () => `${companyStore.company.name} - ${companyStore.company.id}`,
);

const isMoved = ref(false);
const showCopied = ref(false);

const el = useTemplateRef('el');
const { height } = useElementBounding(el);

const top = computed(() => {
  const { headerHeight } = usePageLayoutStore();
  return (headerHeight - height.value) / 2;
});

const left = computed(() => {
  const { navbarWidth } = usePageLayoutStore();
  return isMoved.value ? `${navbarWidth + 16}px` : '50%';
});

const color = computed(() => {
  const { theme } = useThemeStore();
  return theme === 'DARK' ? 'fixed' : 'inverted';
});

const copyToClipboard = () => {
  const textArea = document.createElement('textarea');
  textArea.value = company.value;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);

  showCopied.value = true;
  setTimeout(() => {
    showCopied.value = false;
  }, 2000);
};

const stopImpersonating = () => {
  window.location.href = `${backendUrl}/stop_impersonating`;
};
</script>

<style
  lang="scss"
  module
>
.base {
  position: fixed;
  z-index: 9001;
  width: fit-content;
  transition: left 0.3s $easeOutBack, transform 0.3s $easeOutBack;

  &:not(.moved) {
    transform: translateX(-50%);
  }
}

.inner {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $radius;

  @include theme('light') {
    background-color: var(--warning-base);
  }

  @include theme('dark') {
    background-color: var(--warning-soft);
  }
}

.controls {
  cursor: pointer;
  display: flex;
  padding: $spacing * .5 $spacing * .5;
  gap: $spacing * .5;
}

.right {
  border-left: 1px solid var(--color-bg);
  margin-left: $spacing;
}

.left {
  border-right: 1px solid var(--color-bg);
  margin-right: $spacing;
}
</style>
